import PaginationFooter from './PaginationFooter'
// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

// React
import { useState, useEffect, useImperativeHandle, forwardRef } from 'react'

// Group by
import { groupBy } from 'lodash'

// Dayjs
import dayjs from 'dayjs'

// UI
import ProfileAvatar from '@/components/ui/ProfileAvatar'

// Mantine
import {
    Anchor,
    Select,
    Skeleton,
    Modal,
    Button,
    Tooltip,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

// Classnames
import classNames from 'classnames'

// Icons
import { ArrowNarrowRight, ClockRewind } from '@untitled-ui/icons-react'

const reasons = {
    production_order: 'Assemblage',
    purchase_order: 'Réception bon de commande',
    service_case: 'SAV',
    service_case_out: 'SAV – Utilisation',
    service_case_in: 'SAV – Remise en stock',
    manual: 'Ajustement manuel',
    inventory: 'Inventaire',
    fulfillment: 'Préparation effectuée',
    new_order: 'Commande entrante',
    reception: 'Réception',
    shipment: 'Expédition (Ancien BO)',
}

const reasonFilterOptions = [
    { label: 'Tous', value: 'all' },
    { label: 'Ajustement manuel', value: 'manual' },
    { label: 'Assemblage', value: 'production_order' },
    { label: 'Commande entrante', value: 'new_order' },
    { label: 'Inventaire', value: 'inventory' },
    { label: 'Réception', value: 'reception' },
    { label: 'Préparation effectuée', value: 'fulfillment' },
    { label: 'Réception bon de commande', value: 'purchase_order' },
    { label: 'SAV', value: 'service_case' },
    { label: 'SAV – Utilisation', value: 'service_case_out' },
    { label: 'SAV – Remise en stock', value: 'service_case_in' },
]

const InvlevelLine = ({
    invlevel,
    hideLocation = false,
    hideProductionOrder = false,
    hidePurchaseOrder = false,
    hideServiceCase = false,
    hideComponent = false,
}) => {
    const isPositive = invlevel.evolution > 0

    const evolutionClass = classNames('font-medium', {
        'text-green-500': isPositive,
        'text-red-500': !isPositive,
    })

    return (
        <div className="flex items-center gap-2 p-2 even:bg-gray-50">
            <div className="flex flex-col items-start w-[50px] shrink-0">
                <span className={evolutionClass}>
                    {isPositive ? '+' : ''}
                    {invlevel.evolution}
                </span>

                <div className="flex items-center text-gray-400">
                    <ArrowNarrowRight width={12} height={12} />

                    <span className="text-xs">{invlevel.invlevel}</span>
                </div>
            </div>

            <div className="flex items-start flex-col gap-0 truncate">
                {!hideComponent && (
                    <Anchor
                        className="truncate"
                        href={`/components/${invlevel.component.id}`}
                    >
                        {invlevel.component_name}
                    </Anchor>
                )}

                <div className="flex items-center gap-1">
                    {invlevel.production_order && !hideProductionOrder && (
                        <Anchor
                            href={`/assembly/${invlevel.production_order.id}`}
                            size="sm"
                        >
                            {reasons[invlevel.reason] ?? 'Raison inconnue'} A
                            {invlevel.production_order.id}
                        </Anchor>
                    )}

                    {invlevel.purchase_order && !hidePurchaseOrder && (
                        <Anchor
                            href={`/purchase_orders/${invlevel.purchase_order.id}`}
                            size="sm"
                        >
                            {reasons[invlevel.reason] ?? 'Raison inconnue'} BC
                            {invlevel.purchase_order.id}
                        </Anchor>
                    )}

                    {invlevel.service_case && !hideServiceCase && (
                        <Anchor
                            href={`/service-cases/${invlevel.service_case.id}`}
                            size="sm"
                        >
                            SAV BR{String(invlevel.service_case.id).padStart(5, '0')}
                        </Anchor>
                    )}

                    {!invlevel.production_order && !invlevel.purchase_order && !invlevel.service_case && (
                        <span className="text-gray-900 text-sm">
                            {reasons[invlevel.reason] ?? 'Raison inconnue'}
                        </span>
                    )}
                </div>

                {invlevel.fulfillment?.fulfillment_order_id && (
                    <Anchor
                        href={`/fulfillment_orders/${invlevel.fulfillment.fulfillment_order_id}`}
                        size="sm"
                    >
                        BP{invlevel.fulfillment.fulfillment_order_id}
                    </Anchor>
                )}

                {invlevel.comment && (
                    <span className="text-xs text-gray-500">
                        {invlevel.comment}
                    </span>
                )}

                {!hideLocation && (
                    <div className="text-sm flex items-center gap-2">
                        <span className="text-gray-500">
                            {invlevel.location.name}
                        </span>
                    </div>
                )}
            </div>

            <div className="flex ml-auto items-center gap-5">
                <div className="text-gray-500 text-sm truncate text-right">
                    {dayjs(invlevel.created_at).format('ll')}

                    <div className="text-gray-400">
                        {dayjs(invlevel.created_at).format('HH:mm')}
                    </div>
                </div>

                <ProfileAvatar profile={invlevel.profile} size="md" />
            </div>
        </div>
    )
}

const DisplayDay = ({
    invlevels,
    hideProductionOrder = false,
    hidePurchaseOrder = false,
    hideServiceCase = false,
    hideComponent = false,
    hideLocation = false,
}) => {
    const groupedInvlevels = Object.entries(
        groupBy(invlevels, (item) =>
            dayjs(item.created_at).format('YYYY-MM-DD')
        )
    ).map(([date, invlevels]) => {
        return {
            date,
            invlevels: invlevels.sort((a, b) => {
                return a.component_name.localeCompare(b.component_name)
            }),
        }
    })

    return (
        <div className="flex flex-col gap-5">
            {groupedInvlevels.map((day) => (
                <div key={day.date} className="flex flex-col gap-2">
                    <div className="sticky top-0 bg-white border-b w-full relative z-10">
                        {dayjs(day.date).format('ll')}
                    </div>

                    <div className="flex flex-col border rounded-xl overflow-hidden divide-y">
                        {day.invlevels.map((invlevel) => (
                            <InvlevelLine
                                key={invlevel.id}
                                invlevel={invlevel}
                                hideProductionOrder={hideProductionOrder}
                                hidePurchaseOrder={hidePurchaseOrder}
                                hideServiceCase={hideServiceCase}
                                hideComponent={hideComponent}
                                hideLocation={hideLocation}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    )
}

const perPage = 100

const InvlevelHistory = (
    {
        component_id = null,
        location_id = null,
        production_order_id = null,
        purchase_order_id = null,
        service_case_id = null,
        fulfillment_id = null,
        invtype = null,
        showReasonFilter = false,
        headerActions = null,
        footerActions = null,
        title = 'Historique du stock',
        label = null,
        children = null,
    },
    ref
) => {
    const [opened, { open, close }] = useDisclosure()
    const { requestGet } = useAxiosApi()

    const [invlevels, setInvlevels] = useState([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [reason, setReason] = useState('all')
    const [loading, setLoading] = useState(true)

    const fetchData = async () => {
        setLoading(true)

        try {
            const { data } = await requestGet('/component-invlevels-history', {
                page,
                per_page: perPage,
                order_by: 'created_at',
                order_by_direction: 'desc',
                invtype: invtype ?? 'physical',
                reason,
                component_id,
                location_id,
                production_order_id,
                purchase_order_id,
                service_case_id,
                fulfillment_id,
            })

            setInvlevels(data.data)
            setTotal(data.total)
        } finally {
            setLoading(false)
        }
    }

    // Expose fetch method to parent via ref
    useImperativeHandle(ref, () => ({
        fetch: fetchData,
    }))

    useEffect(() => {
        if (opened) {
            fetchData()
        }
    }, [
        opened,
        component_id,
        location_id,
        production_order_id,
        purchase_order_id,
        service_case_id,
        fulfillment_id,
        invtype,
        page,
        reason,
    ])

    // Reset page when reason filter changes
    useEffect(() => {
        setPage(1)
    }, [reason])

    const invlevelsWithComponentNames = invlevels.map((invlevel) => {
        return {
            ...invlevel,
            component_name:
                invlevel.component.component_category?.name_fr +
                ' – ' +
                invlevel.component.name,
        }
    })

    return (
        <>
            <Modal
                opened={opened}
                size="xl"
                centered
                title={title}
                onClose={close}
            >
                {showReasonFilter && (
                    <Select
                        label="Type de mouvement"
                        value={reason}
                        allowDeselect={false}
                        data={reasonFilterOptions}
                        onChange={(value) => {
                            setReason(value)
                        }}
                        className="mb-3"
                    />
                )}

                {headerActions}

                <div className="h-[70vh] overflow-y-auto">
                    {loading ? (
                        <div className="flex flex-col gap-3 p-3">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Skeleton key={i} height={40} radius="md" />
                            ))}
                        </div>
                    ) : invlevels.length === 0 ? (
                        <div className="flex items-center justify-center h-full text-gray-500">
                            Aucun mouvement de stock associé
                        </div>
                    ) : (
                        <DisplayDay
                            invlevels={invlevelsWithComponentNames}
                            hideProductionOrder={production_order_id !== null}
                            hidePurchaseOrder={purchase_order_id !== null}
                            hideServiceCase={service_case_id !== null}
                            hideComponent={component_id !== null}
                            hideLocation={location_id !== null}
                        />
                    )}
                </div>

                {total > 0 && (
                    <PaginationFooter count={total} page={page} setPage={setPage} perPage={perPage} />
                )}

                {footerActions}
            </Modal>

            {children ? (
                children(open)
            ) : (
                <Tooltip label={title}>
                    <Button
                        variant="default"
                        onClick={open}
                        leftSection={label ? <ClockRewind width={16} height={16} /> : null}
                    >
                        {label || <ClockRewind width={18} height={18} />}
                    </Button>
                </Tooltip>
            )}
        </>
    )
}

export default forwardRef(InvlevelHistory)
